import React, { useState, useRef, useEffect } from 'react';
import './Video.css';

const PROGRESS_REPORT_INTERVAL_MS = 3000;

const Video = ({ videoPath, URL: videoUrl, initialTime = 0, onEndedChange, onProgress }) => {

  const [isPaused, setIsPaused] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [ccOn, setCcOn] = useState(false);
  const [currentLang, setCurrentLang] = useState('English');
  const [maxWatchedTime, setMaxWatchedTime] = useState(initialTime);
  const [hasEnded, setHasEnded] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settingsPanel, setSettingsPanel] = useState('main'); // 'main' or 'speed'
  const [feedback, setFeedback] = useState({ show: false, dir: 'left', text: '' });
  const [isFauxFullscreen, setIsFauxFullscreen] = useState(false);

  const videoRef = useRef(null);
  const wrapperRef = useRef(null);
  const lastTimeRef = useRef(0);
  const feedbackTimeoutRef = useRef(null);
  const lastProgressReportRef = useRef(0);
  const hasEndedRef = useRef(false);
  const maxWatchedTimeRef = useRef(0);
  const hasResumedRef = useRef(false);

  useEffect(() => {
    hasEndedRef.current = hasEnded;
    if (onEndedChange) {
      onEndedChange(hasEnded);
    }
  }, [hasEnded, onEndedChange]);

  useEffect(() => {
    maxWatchedTimeRef.current = maxWatchedTime;
  }, [maxWatchedTime]);

  // watched_timestamp represents the furthest point ever reached, so always report
  // maxWatchedTime here -- never the raw playhead, which can sit behind that max after rewinding
  // to rewatch an earlier part (the backend also clamps this server-side as a safety net).
  const reportProgress = (watchedTime, videoDuration, completed, force = false) => {
    if (!onProgress || !videoDuration) return;
    const now = Date.now();
    if (!force && now - lastProgressReportRef.current < PROGRESS_REPORT_INTERVAL_MS) return;
    lastProgressReportRef.current = now;
    // The backend stores these as (positive) integer seconds, not floating-point.
    onProgress({ watchedTimestamp: Math.round(watchedTime), totalDuration: Math.round(videoDuration), completed });
  };

  // Flush a final progress report on unmount so navigating away mid-video isn't lost
  useEffect(() => {
    const vid = videoRef.current;
    return () => {
      reportProgress(maxWatchedTimeRef.current, vid?.duration, hasEndedRef.current, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Process source for embedding
  const getProcessedSrc = (src) => {
    if (!src) return '';
    if (src.includes('youtube.com') || src.includes('youtu.be')) {
      let videoId = "";
      if (src.includes('watch?v=')) videoId = src.split('watch?v=')[1].split('&')[0];
      else if (src.includes('youtu.be/')) videoId = src.split('youtu.be/')[1].split('?')[0];
      return `https://www.youtube.com/embed/${videoId}?rel=0`;
    } else if (src.includes('drive.google.com')) {
      return src.replace('/view', '/preview');
    }
    return src;
  };

  const finalSrc = getProcessedSrc(videoUrl || videoPath);
  const isIframe = finalSrc.includes('youtube.com/embed/') || finalSrc.includes('drive.google.com');

  const formatTime = (s) => {
    if (isNaN(s)) return "0:00";
    const mins = Math.floor(s / 60);
    const secs = Math.floor(s % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
  };

  const togglePlay = () => {
    if (isIframe) return;
    const vid = videoRef.current;
    if (hasEnded) {
      vid.currentTime = 0;
      vid.play();
    } else if (vid.paused) {
      vid.play();
    } else {
      vid.pause();
    }
  };

  const handleSeek = (e) => {
    if (isIframe) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    const vid = videoRef.current;
    let targetTime = pos * vid.duration;
    
    if (!hasEnded && targetTime > maxWatchedTime) {
      targetTime = Math.max(vid.currentTime, maxWatchedTime);
    }
    vid.currentTime = targetTime;
  };

  const toggleMute = () => {
    if (isIframe) return;
    const vid = videoRef.current;
    vid.muted = !vid.muted;
    setIsMuted(vid.muted);
  };

  const handleVolumeChange = (e) => {
    if (isIframe) return;
    const val = parseFloat(e.target.value);
    const vid = videoRef.current;
    vid.volume = val;
    vid.muted = val === 0;
    setVolume(val);
    setIsMuted(vid.muted);
  };

  const handlePlaybackRateChange = (rate) => {
    if (isIframe) return;
    const vid = videoRef.current;
    vid.playbackRate = parseFloat(rate);
    setPlaybackRate(parseFloat(rate));
  };

  const toggleFullscreen = () => {
    if (isIframe) return;
    if (!document.fullscreenElement) {
      if (wrapperRef.current.requestFullscreen) {
        wrapperRef.current.requestFullscreen();
      } else {
        setIsFauxFullscreen(!isFauxFullscreen);
      }
    } else {
      if (document.exitFullscreen) document.exitFullscreen();
      setIsFauxFullscreen(false);
    }
  };

  const showFeedbackAnim = (dir, text) => {
    setFeedback({ show: true, dir, text });
    if (feedbackTimeoutRef.current) clearTimeout(feedbackTimeoutRef.current);
    feedbackTimeoutRef.current = setTimeout(() => {
      setFeedback(prev => ({ ...prev, show: false }));
    }, 800);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isIframe) return;
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) return;
      
      const key = e.key.toLowerCase();
      const vid = videoRef.current;
      if (!vid) return;

      if (key === 'c') {
        setCcOn(prev => !prev);
      } else if (key === 'd') {
        handlePlaybackRateChange(Math.min(2.0, Number((vid.playbackRate + 0.1).toFixed(2))));
      } else if (key === 'a') {
        handlePlaybackRateChange(Math.max(0.1, Number((vid.playbackRate - 0.1).toFixed(2))));
      } else if (e.code === 'Space' || key === ' ') {
        e.preventDefault();
        togglePlay();
      } else if (key === 'm') {
        toggleMute();
      } else if (key === 'f') {
        toggleFullscreen();
      } else if (key === 'arrowleft') {
        e.preventDefault();
        lastTimeRef.current = vid.currentTime;
        vid.currentTime = Math.max(0, vid.currentTime - 5);
        showFeedbackAnim('left', '-5');
      } else if (key === 'arrowright') {
        e.preventDefault();
        let newTime = vid.currentTime + 5;
        if (!hasEnded && newTime > maxWatchedTime) newTime = Math.max(vid.currentTime, maxWatchedTime);
        lastTimeRef.current = vid.currentTime;
        vid.currentTime = newTime;
        showFeedbackAnim('right', '+5');
      } else if (key === 'arrowup') {
        e.preventDefault();
        vid.muted = false;
        vid.volume = Math.min(1, vid.volume + 0.05);
        setVolume(vid.volume);
        setIsMuted(false);
      } else if (key === 'arrowdown') {
        e.preventDefault();
        vid.muted = false;
        vid.volume = Math.max(0, vid.volume - 0.05);
        setVolume(vid.volume);
        setIsMuted(vid.volume === 0);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isIframe, hasEnded, maxWatchedTime, playbackRate]);

  // Cleanup video element on unmount to release file handles (important for Windows environments)
  useEffect(() => {
    return () => {
      const video = videoRef.current;
      if (!video) return;

      video.pause(); 
      video.removeAttribute('src'); // Remove the source attribute entirely
      video.load(); // Force the player to reset to an empty state
      // Using a micro-task delay to ensure the browser registers the source drop
      setTimeout(() => { if(video) video.src = ""; }, 0);
    };
  }, []);

  if (!finalSrc) return null;

  if (isIframe) {
    return (
      <div className="video-wrapper">
        <iframe
          src={finalSrc}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          title="Video Player"
        />
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className={`video-wrapper ${isFauxFullscreen ? 'faux-fullscreen' : ''} ${isPaused ? 'show-controls' : ''}`}>
      <video
        ref={videoRef} src={finalSrc} playsInline preload="metadata"
        style={{ objectFit: 'contain', width: '100%', height: '100%' }}
        onContextMenu={(e) => e.preventDefault()}
        onPlay={() => { setIsPaused(false); setHasEnded(false); }}
        onPause={() => setIsPaused(true)}
        onEnded={() => {
          setHasEnded(true);
          setIsPaused(true);
          reportProgress(videoRef.current?.duration || maxWatchedTime, videoRef.current?.duration, true, true);
        }}
        onTimeUpdate={() => {
          const vid = videoRef.current;
          if (!vid) return;
          setCurrentTime(vid.currentTime);
          setDuration(vid.duration);
          if (!vid.seeking) lastTimeRef.current = vid.currentTime;
          let nextMaxWatchedTime = maxWatchedTime;
          if (!hasEnded && vid.currentTime > maxWatchedTime) {
            nextMaxWatchedTime = vid.currentTime;
            setMaxWatchedTime(nextMaxWatchedTime);
          }
          reportProgress(nextMaxWatchedTime, vid.duration, hasEnded);
        }}
        onLoadedMetadata={(e) => {
          setDuration(e.currentTarget.duration);
          // Resume from where the user left off, once per mount -- only when there's somewhere
          // to resume to, and not past a video the user already finished.
          if (!hasResumedRef.current) {
            hasResumedRef.current = true;
            if (initialTime > 0 && initialTime < e.currentTarget.duration) {
              e.currentTarget.currentTime = initialTime;
              lastTimeRef.current = initialTime;
            }
          }
          let nextMaxWatchedTime = maxWatchedTime;
          if (e.currentTarget.currentTime > maxWatchedTime) {
            nextMaxWatchedTime = e.currentTarget.currentTime;
            setMaxWatchedTime(nextMaxWatchedTime);
          }
          reportProgress(nextMaxWatchedTime, e.currentTarget.duration, hasEnded, true);
        }}
        onSeeking={() => {
          const vid = videoRef.current;
          if (!vid) return;
          // Only restrict if we are seeking FORWARD past the max watched time
          const isSeekingForward = vid.currentTime > lastTimeRef.current;
          if (!hasEnded && isSeekingForward && vid.currentTime > maxWatchedTime + 1) {
             vid.currentTime = Math.max(0, maxWatchedTime);
          }
        }}
        onClick={togglePlay}
      />
      <div className={`custom-play-btn ${!isPaused ? 'hidden' : ''}`} onClick={togglePlay}>
        {hasEnded ? (
           <div className="replay-icon-wrapper"><svg viewBox="0 0 24 24" width="55%" height="55%" fill="white"><path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"/></svg></div>
        ) : (
          <svg viewBox="0 0 100 100" width="100%" height="100%"><circle cx="50" cy="50" r="45" fill="#3b82f6" stroke="#2563eb" strokeWidth="2" /><polygon points="38,30 72,50 38,70" fill="#ffffff" /></svg>
        )}
      </div>
      <div className={`skip-feedback ${feedback.dir} ${feedback.show ? 'show' : ''}`}>
        {feedback.dir === 'left' && <svg viewBox="0 0 24 24" width="28" height="28" fill="rgba(255,255,255,0.9)"><path d="M11 18V6l-8.5 6 8.5 6zm.5-6l8.5 6V6l-8.5 6z"/></svg>}
        <span>{feedback.text}</span>
        {feedback.dir === 'right' && <svg viewBox="0 0 24 24" width="28" height="28" fill="rgba(255,255,255,0.9)"><path d="M4 18l8.5-6L4 6v12zm9-12v12l8.5-6L13 6z"/></svg>}
      </div>
      <div className="custom-vid-controls" style={{ zIndex: 10 }} onClick={(e) => e.stopPropagation()}>
        <div className="vid-prog-wrap" onClick={handleSeek}>
          <div className="vid-prog-fill" style={{ width: `${(currentTime / duration) * 100 || 0}%` }} />
        </div>
        <div className="vid-bottom-row">
          <div className="vid-left-controls">
            <button className="vid-icon-btn" onClick={togglePlay}>
              {hasEnded ? <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"/></svg> : isPaused ? <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg> : <svg viewBox="0 0 24 24" fill="currentColor"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>}
            </button>
            <div className="vol-container">
              <button className="vid-icon-btn" onClick={toggleMute}>
                {isMuted || volume === 0 ? <svg viewBox="0 0 24 24" fill="currentColor"><path d="M11 1.4L15.4 9 17 10.6 18.6 9 20 10.4 18.4 12 20 13.6 18.6 15 17 13.4 15.4 15 14 13.6 15.6 12 14 10.4zM3 9v6h4l5 5V4L7 9H3z"/></svg> : volume < 0.5 ? <svg viewBox="0 0 24 24" fill="currentColor"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/></svg> : <svg viewBox="0 0 24 24" fill="currentColor"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/></svg>}
              </button>
              <div className="vol-slider-wrap"><input type="range" className="vol-slider" min="0" max="1" step="any" value={isMuted ? 0 : volume} onInput={handleVolumeChange} style={{ background: `linear-gradient(to right, white ${(isMuted ? 0 : volume) * 100}%, rgba(255,255,255,0.3) ${(isMuted ? 0 : volume) * 100}%)` }}/></div>
            </div>
            <div className="vid-time-txt">{formatTime(currentTime)} / {formatTime(duration)}</div>
          </div>
          <div className="vid-right-controls">
            <button className={`vid-icon-btn cc-btn ${ccOn ? 'active-cc' : ''}`} onClick={() => setCcOn(!ccOn)}>C</button>
            <button className="vid-icon-btn" onClick={() => setShowSettings(!showSettings)}><svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 9.5c1.38 0 2.5 1.12 2.5 2.5s-1.12 2.5-2.5 2.5-2.5-1.12-2.5-2.5 1.12-2.5 2.5-2.5m0-1c-1.93 0-3.5 1.57-3.5 3.5s1.57 3.5 3.5 3.5 3.5-1.57 3.5-3.5-1.57-3.5-3.5-3.5zM13.22 3l.55 2.2.13.51.5.18c.61.23 1.19.56 1.72.98l.4.32.5-.14 2.17-.62 1.22 2.11-1.63 1.59-.37.36.08.51c.05.32.08.64.08.98s-.03.66-.08.98l-.08.51.37.36 1.63 1.59-1.22 2.11-2.17-.62-.5-.14-.4.32c-.53.43-1.11.76-1.72.98l-.5.18-.13.51-.55 2.2h-2.44l-.55-2.2-.13-.51-.5-.18c-.61-.23-1.19-.56-1.72-.98l-.4-.32-.5.14-2.17.62-1.22-2.11 1.63-1.59.37-.36-.08-.51c-.05-.32-.08-.64-.08-.98s.03-.66.08-.98l.08-.51-.37-.36-1.63-1.59 1.22-2.11 2.17.62.5.14.4-.32c.53-.43 1.11-.76 1.72-.98l.5-.18.13-.51.55-2.2h2.44M14 2h-4l-.74 2.96c-.73.27-1.4.66-2 1.14l-2.92-.83-2 3.46 2.19 2.13c-.06.37-.09.75-.09 1.14s.03.77.09 1.14l-2.19 2.13 2 3.46 2.92-.83c.6.48 1.27.87 2 1.14L10 22h4l.74-2.96c.73-.27 1.4-.66 2-1.14l2.92.83 2-3.46-2.19-2.13c.06-.37.09-.75.09-1.14s-.03-.77-.09-1.14l2.19-2.13-2-3.46-2.92.83c-.6-.48-1.27-.87-2-1.14L14 2z"/></svg></button>
            <button className="vid-icon-btn" onClick={toggleFullscreen}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path></svg></button>
            {showSettings && (
              <div className="vid-settings-menu show">
                {settingsPanel === 'main' ? (
                  <div className="vid-panel">
                    <div className="menu-row" onClick={() => setCcOn(!ccOn)}><div className="menu-row-label">Subtitles / CC</div><div className={`vid-toggle ${ccOn ? 'on' : ''}`} /></div>
                    {ccOn && <div className="lang-btn-group">{['English', 'Français', 'العربية'].map(l => <button key={l} className={`lang-btn ${currentLang === l ? 'active' : ''}`} onClick={(e) => { e.stopPropagation(); setCurrentLang(l); }}>{l}</button>)}</div>}
                    <div className="menu-row" onClick={() => setSettingsPanel('speed')}><div className="menu-row-label">Playback Speed</div><div style={{color:'#94a3b8'}}>{playbackRate.toFixed(2)}x ❯</div></div>
                  </div>
                ) : (
                  <div className="vid-panel">
                    <div className="panel-header" onClick={() => setSettingsPanel('main')}>❮ Playback Speed</div>
                    <div className="spd-val">{playbackRate.toFixed(2)}x</div>
                    <div className="spd-slider-container"><input type="range" min="0.1" max="2.0" step="0.05" value={playbackRate} onInput={(e) => handlePlaybackRateChange(e.target.value)} /></div>
                    <div className="preset-speeds">{[1, 1.25, 1.5, 2.0].map(r => <button key={r} className={`lang-btn ${playbackRate === r ? 'active' : ''}`} onClick={(e) => { e.stopPropagation(); handlePlaybackRateChange(r); }}>{r}x</button>)}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className={`vid-captions ${ccOn ? 'show' : ''}`}>[{currentLang} subtitles playing...]</div>
    </div>
  );
};

export default Video;